import { FC } from 'react';
import { Col, DatePicker, Divider, Form, Input, Row } from 'antd';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { CustomerManagement } from '@/libs/fleet-api/services/types';
import dayjs from 'dayjs';
import TextArea from 'antd/es/input/TextArea';
import rule from '@/libs/antd/rules';

export const useManagerProfileForm = (
	onSubmit: (values: CustomerManagement) => void,
) => {
	const forms = useForm<CustomerManagement>();

	return {
		...forms,
		onSubmit,
	};
};

const ManagerProfileForm: FC<ReturnType<typeof useManagerProfileForm>> = ({
	form,
	value,
	fieldName,
	onSubmit,
}) => {
	return (
		<Form
			{...ANTD_DEFAULT_PROPS.FORM}
			form={form}
			onFinish={onSubmit}
			style={{ marginTop: 20 }}
			labelCol={{ span: 7 }}
			wrapperCol={{ span: 17 }}
		>
			<Form.Item
				name={fieldName('id')}
				noStyle
				initialValue={value?.id}
			/>
			<Form.Item
				name={fieldName('identityCardType')}
				noStyle
				initialValue={value?.identityCardType}
			/>
			<Form.Item
				name={fieldName('dateOfBirth')}
				noStyle
				initialValue={value?.dateOfBirth}
			/>
			<Form.Item
				name={fieldName('address')}
				noStyle
				initialValue={value?.address}
			/>
			<Form.Item
				name={fieldName('rt')}
				noStyle
				initialValue={value?.rt}
			/>
			<Form.Item
				name={fieldName('rw')}
				noStyle
				initialValue={value?.rw}
			/>
			<Form.Item
				name={fieldName('phoneNumber')}
				noStyle
				initialValue={value?.phoneNumber}
			/>
			<Form.Item
				name={fieldName('isShareholder')}
				noStyle
				initialValue={value?.isShareholder}
			/>
			<Form.Item
				name={fieldName('shareholding')}
				noStyle
				initialValue={value?.shareholding}
			/>
			<Form.Item
				name={fieldName('citizenship')}
				noStyle
				initialValue={value?.citizenship}
			/>
			<Form.Item
				name={fieldName('nationality')}
				noStyle
				initialValue={value?.nationality}
			/>
			<Form.Item
				name={fieldName('position')}
				noStyle
				initialValue={value?.position}
			/>
			<Form.Item
				name={fieldName('postal')}
				noStyle
				initialValue={value?.postal}
			/>
			<Form.Item
				name={fieldName('identityFile')}
				noStyle
				initialValue={value?.identityFile}
			/>
			<Form.Item
				name={fieldName('status')}
				noStyle
				initialValue={value?.status}
			/>

			<Row gutter={[40, 20]}>
				<Col span={12}>
					<Form.Item
						name={fieldName('name')}
						label="Name"
						initialValue={value?.name}
					>
						<Input disabled />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item label="Jenis Identitas">
						<Input
							value={value?.identityCardType?.value}
							disabled
						/>
					</Form.Item>
				</Col>
			</Row>
			<Divider />
			<Row gutter={[40, 20]}>
				<Col span={12}>
					<Form.Item
						name={fieldName('identityNumber')}
						label="No.ID"
						initialValue={value?.identityNumber}
					>
						<Input disabled />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						name={fieldName('startManagementPeriod')}
						label="Periode Kepengurusan"
						initialValue={value?.startManagementPeriod}
						getValueProps={(value) => ({
							value: value && dayjs(value),
						})}
						normalize={(value) => value && dayjs(value).utc(true).toISOString()}
						rules={[
							rule.required(),
							{
								validator: (_, value) => {
									const endDate = form.getFieldValue(
										fieldName('endManagementPeriod'),
									);
									if (
										value &&
										endDate &&
										dayjs(value).isAfter(dayjs(endDate))
									) {
										return Promise.reject(
											new Error(
												'Tanggal Mulai tidak boleh lebih besar dari Tanggal Berakhir',
											),
										);
									}
									return Promise.resolve();
								},
							},
						]}
					>
						<DatePicker />
					</Form.Item>
				</Col>
			</Row>
			<Row
				gutter={[40, 20]}
				style={{ marginTop: 20 }}
			>
				<Col span={12}></Col>
				<Col span={12}>
					<Form.Item
						name={fieldName('endManagementPeriod')}
						label=" "
					>
						<Form.Item
							name={fieldName('endManagementPeriod')}
							label=""
							initialValue={value?.endManagementPeriod}
							getValueProps={(value) => ({
								value: value && dayjs(value),
							})}
							normalize={(value) =>
								value && dayjs(value).utc(true).toISOString()
							}
							rules={[
								rule.required(),
								{
									validator: (_, value) => {
										const startDate = form.getFieldValue(
											fieldName('startManagementPeriod'),
										);
										if (
											value &&
											startDate &&
											dayjs(value).isBefore(dayjs(startDate))
										) {
											return Promise.reject(
												new Error(
													'Tanggal Berakhir tidak boleh kurang dari Tanggal Mulai',
												),
											);
										}
										return Promise.resolve();
									},
								},
							]}
						>
							<DatePicker />
						</Form.Item>
					</Form.Item>
				</Col>
			</Row>
			<Divider />
			<Form.Item
				name={fieldName('profesionalExperience')}
				label="Pengalaman Professional"
				initialValue={value?.profesionalExperience}
				style={{ marginTop: 20 }}
				rules={[rule.required()]}
			>
				<TextArea rows={5} />
			</Form.Item>
		</Form>
	);
};

export default ManagerProfileForm;
